import { devLog } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProfessionalCard from '../components/ProfessionalCard';
import { Search, Filter, X, Loader2, Heart, Briefcase, Sparkles, Leaf } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const professionalTypes = [
  { value: 'therapist', label: 'Terapeutas', icon: Heart },
  { value: 'coach', label: 'Mentores', icon: Briefcase },
  { value: 'spiritualist', label: 'Espiritualistas', icon: Sparkles }
];

export default function ProfessionalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specializations, setSpecializations] = useState({});
  const [approaches, setApproaches] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    specialization: searchParams.get('specialization') || '',
    approach: searchParams.get('approach') || ''
  });

  useEffect(() => {
    fetchSpecializations();
    fetchApproaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProfessionals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchSpecializations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/specializations`);
      setSpecializations(data);
    } catch (err) {
      devLog.error(err);
    }
  };

  const fetchApproaches = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/approaches`);
      setApproaches(data);
    } catch (err) {
      devLog.error(err);
    }
  };

  const fetchProfessionals = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('professional_type', filters.type);
      if (filters.specialization) params.append('specialization', filters.specialization);
      if (filters.approach) params.append('approach', filters.approach);

      const { data } = await axios.get(`${API_URL}/api/professionals?${params.toString()}`);
      setProfessionals(data);
    } catch (err) {
      setError('Erro ao carregar profissionais');
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ type: '', specialization: '', approach: '' });
    setSearchParams({});
  };

  const hasActiveFilters = filters.type || filters.specialization || filters.approach;
  const currentSpecs = filters.type ? specializations[filters.type] : [];
  const currentApproaches = filters.type ? approaches[filters.type] : [];

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />

      <main className="flex-1 py-12" data-testid="professionals-page">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl sm:text-4xl font-light text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Encontre seu profissional
            </h1>
            <p className="text-[#63635E]">
              Explore nossa comunidade de profissionais verificados
            </p>
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 mb-6 text-[#4A5D4E] font-medium"
            data-testid="filter-toggle"
          >
            <Filter className="w-5 h-5" strokeWidth={1.5} />
            {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
          </button>

          <div className="flex flex-col md:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className={`md:w-72 flex-shrink-0 ${showFilters ? 'block' : 'hidden md:block'}`}>
              <div className="card-theraflow p-6 sticky top-24" data-testid="filters-panel">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-medium text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Filtros
                  </h2>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-[#4A5D4E] hover:underline flex items-center gap-1"
                      data-testid="clear-filters"
                    >
                      <X className="w-4 h-4" />
                      Limpar
                    </button>
                  )}
                </div>

                {/* Professional Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#292927] mb-3">
                    Tipo de Profissional
                  </label>
                  <div className="space-y-2">
                    {professionalTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => handleFilterChange('type', filters.type === type.value ? '' : type.value)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                          filters.type === type.value
                            ? 'bg-[#4A5D4E] text-white'
                            : 'bg-[#F6EBE6] text-[#292927] hover:bg-[#EBDCD5]'
                        }`}
                        data-testid={`filter-type-${type.value}`}
                      >
                        <type.icon className="w-5 h-5" strokeWidth={1.5} />
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Specialization */}
                {currentSpecs?.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#292927] mb-3">
                      Especialização
                    </label>
                    <select
                      value={filters.specialization}
                      onChange={(e) => handleFilterChange('specialization', e.target.value)}
                      className="input-theraflow"
                      data-testid="filter-specialization"
                    >
                      <option value="">Todas</option>
                      {currentSpecs.map((spec) => (
                        <option key={spec} value={spec}>{spec}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Approach */}
                {currentApproaches?.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#292927] mb-3">
                      Abordagem
                    </label>
                    <select
                      value={filters.approach}
                      onChange={(e) => handleFilterChange('approach', e.target.value)}
                      className="input-theraflow"
                      data-testid="filter-approach"
                    >
                      <option value="">Todas</option>
                      {currentApproaches.map((approach) => (
                        <option key={approach} value={approach}>{approach}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* AI Match CTA */}
                <div className="pt-6 border-t border-[#EAE8E3]">
                  <Link
                    to="/match"
                    className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                    data-testid="ai-match-cta"
                  >
                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                    Match com IA
                  </Link>
                  <p className="mt-3 text-xs text-[#A0A09A] text-center">
                    Deixe a IA encontrar o profissional ideal
                  </p>
                </div>
              </div>
            </aside>

            {/* Results */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-[#4A5D4E] animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <p className="text-[#B85C5C]">{error}</p>
                  <button
                    onClick={fetchProfessionals}
                    className="mt-4 btn-secondary"
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : professionals.length === 0 ? (
                <div className="text-center py-20">
                  <Search className="w-12 h-12 mx-auto text-[#A0A09A] mb-4" strokeWidth={1.5} />
                  <h3 className="text-xl font-medium text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Nenhum profissional encontrado
                  </h3>
                  <p className="text-[#63635E] mb-6">
                    Tente ajustar seus filtros ou explore outras categorias
                  </p>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="btn-secondary">
                      Limpar filtros
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm text-[#63635E] mb-6" data-testid="results-count">
                    {professionals.length} profissional{professionals.length !== 1 ? 'is' : ''} encontrado{professionals.length !== 1 ? 's' : ''}
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {professionals.map((prof, idx) => (
                      <div 
                        key={prof.id} 
                        className="animate-fade-in"
                        style={{ animationDelay: `${idx * 0.05}s` }}
                      >
                        <ProfessionalCard professional={prof} />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
